import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { apiFetch, API } from "@/utils/api";

// Pages
import Login from "@/pages/Login";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import LegalTerms from "@/pages/LegalTerms";
import LegalPrivacy from "@/pages/LegalPrivacy";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import BankTransactions from "@/pages/BankTransactions";
import BankRules from "@/pages/BankRules";
import Reconciliation from "@/pages/Reconciliation";
import InvoicesReceived from "@/pages/InvoicesReceived";
import InvoicesEmitted from "@/pages/InvoicesEmitted";
import ChartOfAccounts from "@/pages/ChartOfAccounts";
import Journal from "@/pages/Journal";
import Reports from "@/pages/Reports";
import Exports from "@/pages/Exports";
import Documents from "@/pages/Documents";
import Payrolls from "@/pages/Payrolls";
import Clients from "@/pages/Clients";
import Subscriptions from "@/pages/Subscriptions";
import Alerts from "@/pages/Alerts";
import Budgets from "@/pages/Budgets";
import Settings from "@/pages/Settings";
import MobileTaxes from "@/mobile/MobileTaxes";
import MobileMore from "@/mobile/MobileMore";

// Layout
import Layout from "@/components/Layout";

// Protected Route Component
function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await apiFetch(`${API}/auth/me`);
        if (!response.ok) throw new Error("Not authenticated");
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [location.state, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout user={user}>{children}</Layout>;
}

// App Router
function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* A5: páginas legales públicas, enlazadas desde el login */}
      <Route path="/terminos" element={<LegalTerms />} />
      <Route path="/privacidad" element={<LegalPrivacy />} />
      <Route path="/auth/callback" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/banco/movimientos"
        element={
          <ProtectedRoute>
            <BankTransactions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/banco/reglas"
        element={
          <ProtectedRoute>
            <BankRules />
          </ProtectedRoute>
        }
      />
      <Route
        path="/conciliacion"
        element={
          <ProtectedRoute>
            <Reconciliation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/facturas/recibidas"
        element={
          <ProtectedRoute>
            <InvoicesReceived />
          </ProtectedRoute>
        }
      />
      <Route
        path="/facturas/emitidas"
        element={
          <ProtectedRoute>
            <InvoicesEmitted />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contabilidad/plan"
        element={
          <ProtectedRoute>
            <ChartOfAccounts />
          </ProtectedRoute>
        }
      />
      <Route path="/nominas" element={<ProtectedRoute><Payrolls /></ProtectedRoute>} />
      <Route
        path="/contabilidad/diario"
        element={
          <ProtectedRoute>
            <Journal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/informes"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/exportar"
        element={
          <ProtectedRoute>
            <Exports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentos"
        element={
          <ProtectedRoute>
            <Documents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientes"
        element={
          <ProtectedRoute>
            <Clients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/suscripciones"
        element={
          <ProtectedRoute>
            <Subscriptions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/alertas"
        element={
          <ProtectedRoute>
            <Alerts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/presupuestos"
        element={
          <ProtectedRoute>
            <Budgets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ajustes"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/impuestos"
        element={
          <ProtectedRoute>
            <MobileTaxes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mas"
        element={
          <ProtectedRoute>
            <MobileMore />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
